<template>
  <Modal
    :id="uid"
    v-model:visible="isVisible"
    class="tracking-link-modal"
    :header="
      t('common.shareParam', {
        param: t('shipments.trackingLink'),
      })
    "
    @keypress.esc="isVisible = false"
  >
    <OrderTrackingInfo
      v-for="order in orders"
      :id="`${uid}-order-${order.id}`"
      :key="order.id"
      :order-id="order.id"
      :suggested-emails="order.suggestedEmails"
      :url="order.url"
      :loading="sending[order.id]"
      @share-via-email="shareViaEmail(order.id, $event)"
    />
  </Modal>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';

import { SharingRequest } from '@/model/SharingRequest';

import OrderTrackingInfo from '@/components/shipment/tracking/OrderTrackingInfo.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

type OrderData = {
  id: number;
  url: string;
  suggestedEmails: string[];
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  orders: {
    type: Array as PropType<OrderData[]>,
    default: () => [],
  },
});
const { orders } = toRefs(props);
const emit = defineEmits<{
  (
    e: 'share-via-email',
    payload: {
      sharingRequest: SharingRequest;
      orderId: number;
      confirmEmailSent: () => void;
    },
  ): void;
}>();

const { t } = useI18n();
const { uid } = useUid('tracking-link-modal');

const sending: Ref<{ [orderId: number]: boolean }> = ref({});

const shareViaEmail = (orderId: number, sharingRequest: SharingRequest) => {
  sending.value[orderId] = true;
  emit('share-via-email', {
    sharingRequest,
    orderId,
    confirmEmailSent: () => (sending.value[orderId] = false),
  });
};
</script>

<style>
.tracking-link-modal.p-dialog {
  .p-dialog-header {
    padding-bottom: 0;
  }

  .order-tracking-info + .order-tracking-info {
    margin-top: var(--sp-6);
    border-top: var(--divider);
  }
}
</style>
